/// <amd-module name="Analytics/Management/AnalyticsManagement" />
import { AnalyticsState } from "Analytics/Common/Contracts/Analytics";
import { getAnalyticsClient, IAnalyticsRestClient } from "Analytics/Common/RestClient/Analytics";
import * as Resources from "Analytics/Management/Resources";
import React = require("react");
import { TimerManagement } from "VSS/Core/TimerManagement";
import { announce } from "VSS/Core/Util/Accessibility";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Header, TitleSize } from "VSSUI/Header";
import { Image } from "VSSUI/Image";
import { Link } from "VSSUI/Link";
import { Page } from "VSSUI/Page";
import { Statuses } from "VSSUI/Status";
import { Toast } from "VSSUI/Toast";
import { AnalyticsDialogComponent, IAnalyticsDialogProps } from "./AnalyticsDialogComponent";
import { AnalyticsStatusComponent, IAnalyticsStatusProps } from "./AnalyticsStatusComponent";

interface IAnalyticsManagementState {
    currentAnalyticsState: AnalyticsState;
    requestedAnalyticsState?: AnalyticsState;
    analyticsStateChangedDate: Date;
    estimatedDiskSpaceRequiredMiB?: number;
    isDialogOpen: Boolean;
    errorText?: string;
}

class AnalyticsManagementComponent extends VssComponent<{}, IAnalyticsManagementState> {
    private static readonly stateButtonClassName = "analytics-state-change-button";
    private timerManagement = new TimerManagement();
    private analyticsClient: IAnalyticsRestClient;

    public async componentDidMount(): Promise<void> {
        this.timerManagement.setInterval(() => {
            this.pollServerForCurrentAnalyticsState();
        }, 40000);
        this.analyticsClient = getAnalyticsClient(this.context.pageContext);

        const { analyticsState, changedDate } = await this.analyticsClient.getState();

        const state: IAnalyticsManagementState = {
            currentAnalyticsState: analyticsState,
            analyticsStateChangedDate: changedDate,
            isDialogOpen: false
        };

        this.setState(state);
    }

    public async componentDidUpdate(prevProps: {}, prevState: IAnalyticsManagementState): Promise<void> {
        if (
            this.state != null &&
            this.state.currentAnalyticsState != null &&
            this.state.currentAnalyticsState === AnalyticsState.Disabled &&
            (prevState == null || prevState.currentAnalyticsState !== AnalyticsState.Disabled)
        ) {
            const estimatedDiskSpaceRequiredMiB = await this.analyticsClient.getSpaceRequirements();
            const newState: Pick<IAnalyticsManagementState, "estimatedDiskSpaceRequiredMiB"> = { estimatedDiskSpaceRequiredMiB };
            this.setState(newState);
        }

        // Screen reader announcement of status
        if (
            this.state != null &&
            (prevState == null ||
                this.state.currentAnalyticsState !== prevState.currentAnalyticsState ||
                this.state.currentAnalyticsState === AnalyticsState.Preparing ||
                this.state.currentAnalyticsState === AnalyticsState.Deleting)
        ) {
            const statusProps = this.generateStatusProps();
            const assertive = false;
            announce(statusProps.statusExpandedText, assertive);
        }
    }

    public render(): JSX.Element | null {
        if (!this.state) {
            return null;
        }

        return (
            <Page className="analytics-management-page">
                <Header title={Resources.AnalyticsTitleText} titleSize={TitleSize.Large} />
                <div className="page-content">
                    {this.renderDescription()}
                    <div className="analytics-management-overview-divider" />
                    {this.renderBodyContent()}

                    {this.state.isDialogOpen ? this.renderConfirmationDialog() : null}
                    {this.state.errorText ? this.renderToastBar() : null}
                </div>
            </Page>
        );
    }

    private renderDescription(): JSX.Element {
        return (
            <FormatComponent className="analytics-management-description" format={Resources.AnalyticsDescription}>
                <Link href={Resources.LearnMoreLinkUrl} target="_blank">
                    {Resources.LearnMoreLinkText}
                </Link>
            </FormatComponent>
        );
    }

    private renderBodyContent(): JSX.Element {
        return (
            <div className="flex-column flex-center flex-grow flex-wrap">
                <Image
                    className="analytics-management-bars-image"
                    width={200}
                    src={getAssetLocation(
                        this.context.pageContext,
                        "ms.vss-analytics/analytics-management-content/assets/choicegroup-bar-selected.png"
                    )}
                    alt="" // Intentionally empty to inform screenreaders this image is not worth mentioning.
                />
                <AnalyticsStatusComponent {...this.generateStatusProps()} />
                {this.renderStateChangeButtons()}
                {this.renderEstimatedDiskSpaceRequired()}
            </div>
        );
    }

    private renderStateChangeButtons(): JSX.Element {
        return (
            <div className="flex-row flex-grow flex-wrap">
                {this.state.currentAnalyticsState === AnalyticsState.Preparing || this.state.currentAnalyticsState === AnalyticsState.Enabled
                    ? this.renderStateChangeButton(Resources.PauseButtonText, ButtonType.Normal, AnalyticsState.Paused)
                    : null}

                {this.state.currentAnalyticsState === AnalyticsState.Paused
                    ? this.renderStateChangeButton(Resources.ResumeButtonText, ButtonType.Primary, AnalyticsState.Enabled)
                    : null}

                {this.state.currentAnalyticsState === AnalyticsState.Disabled ? this.renderEnableButton() : null}

                {this.state.currentAnalyticsState === AnalyticsState.Preparing ||
                this.state.currentAnalyticsState === AnalyticsState.Enabled ||
                this.state.currentAnalyticsState === AnalyticsState.Paused
                    ? this.renderStateChangeButton(Resources.DisableButtonText, ButtonType.Danger, AnalyticsState.Disabled)
                    : null}
            </div>
        );
    }

    private renderStateChangeButton(buttonText: string, buttonType: ButtonType, requestedAnalyticsState: AnalyticsState): JSX.Element {
        return (
            <Button
                className={AnalyticsManagementComponent.stateButtonClassName}
                text={buttonText}
                primary={buttonType === ButtonType.Primary}
                danger={buttonType === ButtonType.Danger}
                onClick={() => {
                    this.setState({ requestedAnalyticsState: requestedAnalyticsState, isDialogOpen: true, errorText: undefined });
                }}
            />
        );
    }

    private renderEnableButton(): JSX.Element {
        return (
            <Button
                className={AnalyticsManagementComponent.stateButtonClassName}
                text={Resources.EnableButtonText}
                primary={true}
                onClick={() => {
                    this.submitRequestedState(AnalyticsState.Enabled);
                }}
            />
        );
    }

    private renderEstimatedDiskSpaceRequired(): JSX.Element | null {
        if (this.state.estimatedDiskSpaceRequiredMiB != null && this.state.currentAnalyticsState === AnalyticsState.Disabled) {
            const estimateInGiB = Math.ceil(this.state.estimatedDiskSpaceRequiredMiB / 1024); // MiB to GiB
            const displayValue =
                this.state.estimatedDiskSpaceRequiredMiB < 500 && this.state.estimatedDiskSpaceRequiredMiB > 0
                    ? `< ${estimateInGiB} GB`
                    : `${estimateInGiB} GB`;

            return (
                <FormatComponent className="disk-space-estimate" format={Resources.EstimatedDiskSpaceRequired}>
                    <b>{displayValue}</b>
                </FormatComponent>
            );
        }

        return null;
    }

    private renderConfirmationDialog(): JSX.Element {
        return <AnalyticsDialogComponent {...this.generateDialogProps()} />;
    }

    private generateStatusProps(): IAnalyticsStatusProps {
        let statusProps: IAnalyticsStatusProps;

        switch (this.state.currentAnalyticsState) {
            case AnalyticsState.Enabled:
                statusProps = {
                    statusType: { ...Statuses.Success },
                    statusText: Resources.AnalyticsEnabled,
                    statusExpandedText: Resources.AnalyticsEnabledDetails
                };
                break;
            case AnalyticsState.Preparing:
                statusProps = {
                    statusType: { ...Statuses.Running },
                    statusText: Resources.AnalyticsDataPreparing,
                    statusExpandedText: Resources.AnalyticsDataPreparingDetails
                };
                break;
            case AnalyticsState.Paused:
                statusProps = {
                    statusType: { ...Statuses.Warning },
                    statusText: Resources.AnalyticsPaused,
                    statusExpandedText: Resources.AnalyticsPausedDetails,
                    pausedDate: this.state.analyticsStateChangedDate
                };
                break;
            case AnalyticsState.Deleting:
                statusProps = {
                    statusType: { ...Statuses.Running },
                    statusText: Resources.AnalyticsDisabling,
                    statusExpandedText: Resources.AnalyticsDisablingDetails
                };
                break;
            case AnalyticsState.Disabled:
                statusProps = {
                    statusType: { ...Statuses.Failed },
                    statusText: Resources.AnalyticsDisabled,
                    statusExpandedText: Resources.AnalyticsDisabledDetails
                };
                break;
            default:
                throw new Error("An invalid current state of Analytics was discovered: '" + this.state.currentAnalyticsState + "'.");
        }

        return statusProps;
    }

    private generateDialogProps(): IAnalyticsDialogProps {
        let dialogProps: Partial<IAnalyticsDialogProps> = {};

        if (this.state.requestedAnalyticsState === AnalyticsState.Paused) {
            dialogProps = {
                dialogTitleText: Resources.PauseDialogTitle,
                dialogBodyText: Resources.PauseDialogBody,
                featuresListDescription: Resources.PauseDisableFeaturesListDescription,
                confirmButtonText: Resources.PauseButtonText,
                learnMoreLinkText: Resources.PauseDialogLinkText,
                isSubmitButtonPrimary: true,
                isSubmitButtonDanger: false
            };
        } else if (this.state.requestedAnalyticsState === AnalyticsState.Enabled && this.state.currentAnalyticsState === AnalyticsState.Paused) {
            dialogProps = {
                dialogTitleText: Resources.ResumeDialogTitle,
                dialogBodyText: Resources.ResumeDialogBody,
                featuresListDescription: Resources.ResumeFeaturesListDescription,
                confirmButtonText: Resources.ResumeButtonText,
                learnMoreLinkText: Resources.ResumeDialogLinkText,
                isSubmitButtonPrimary: true,
                isSubmitButtonDanger: false
            };
        } else if (this.state.requestedAnalyticsState === AnalyticsState.Disabled) {
            dialogProps = {
                dialogTitleText: Resources.DisableDialogTitle,
                dialogBodyText: Resources.DisableDialogBody,
                featuresListDescription: Resources.PauseDisableFeaturesListDescription,
                confirmButtonText: Resources.DisableButtonText,
                learnMoreLinkText: Resources.DisableDialogLinkText,
                isSubmitButtonPrimary: false,
                isSubmitButtonDanger: true
            };
        } else {
            throw new Error(
                "An invalid combination of current and requested states of Analytics was discovered. Current state: '" +
                    this.state.currentAnalyticsState +
                    "' Requested state: '" +
                    this.state.requestedAnalyticsState +
                    "'."
            );
        }

        dialogProps.onCancel = this.onDismissDialog;
        dialogProps.onSubmit = () => this.submitRequestedState(this.state.requestedAnalyticsState as AnalyticsState);

        return dialogProps as IAnalyticsDialogProps;
    }

    private onDismissDialog = () => {
        this.setState({ isDialogOpen: false, requestedAnalyticsState: undefined });
    };

    private async submitRequestedState(requestedState: AnalyticsState) {
        let errorMessage = null;
        try {
            await this.analyticsClient.updateState(requestedState);
        } catch (error) {
            if (error && error.serverError && error.serverError.typeKey === "InvalidAccessException") {
               errorMessage = Resources.PermissionError;
            } else if (error && error.message) {
                console.error(JSON.stringify(error.message));
            } else if (error) {
                console.error(JSON.stringify(error));
            }
        }

        if (errorMessage != null) {
            this.setState({errorText: errorMessage, isDialogOpen: false, requestedAnalyticsState: undefined});
        } else {
            const currentState = this.getCurrentState(requestedState);
            const state: IAnalyticsManagementState = {
                currentAnalyticsState: currentState,
                requestedAnalyticsState: undefined,
                analyticsStateChangedDate: new Date(),
                isDialogOpen: false
            };

            this.setState(state);
        }
    }

    private getCurrentState(requestedState: AnalyticsState) {
        // If setting the state did not fail, the state is now either in a transitioning state or the requested state.
        // The alternative is to get the state from the server immediately following setting it, which may return the
        // previous state due to caching behavior. The periodic polling operation will kick in shortly and begin
        // displaying the actual state from the server.

        switch (requestedState) {
            case AnalyticsState.Enabled:
                requestedState = AnalyticsState.Preparing;
                break;
            case AnalyticsState.Disabled:
                requestedState = AnalyticsState.Deleting;
                break;
            default:
        }

        return requestedState;
    }

    private async pollServerForCurrentAnalyticsState() {
        const currentAnalyticsStateFromServer = await this.analyticsClient.getState();

        this.setState({
            currentAnalyticsState: currentAnalyticsStateFromServer.analyticsState,
            analyticsStateChangedDate: currentAnalyticsStateFromServer.changedDate,
            errorText: undefined
        });
    }

    private renderToastBar() : JSX.Element | null {
        if (this.state.errorText)
        {
            return (
                <Toast
                    message= {this.state.errorText}
                />
            );
        }

        return null;
    }
}

enum ButtonType {
    Normal,
    Primary,
    Danger
}

Components.add("analyticsManagement", AnalyticsManagementComponent);
